const BASE = '/myf-api';

export interface LocalizedString {
  it: string;
  en: string;
  de: string;
  es: string;
  fr: string;
}

export interface CommercialConditionsResponse {
  // Contratto concordato con Foscarini: null quando il cliente non ha condizioni
  // (visto live su wsprod per Foscarini INC./C01614).
  commercialConditions: {
    baseDiscount: number;
    totalBonusDiscount: number | null;
    paymentMethod: LocalizedString;
  } | null;
  shippingPolicies: {
    freeShippingThreshold: number;
    dropshipFee: number;
  };
  // `deadline` (ISO YYYY-MM-DD) e `color` arrivano dal backend (verificato su
  // wsprod 2026-09-21); restano opzionali perche' possono essere null o vuoti.
  // `color` vuoto = semaforo automatico sulla percentuale (vedi ProgressBar).
  shippingBonus: {
    import: number;
    target: number;
    deadline?: string | null;
    color?: string | null;
  } | null;
  bonusProgram: Array<{
    step: string;
    import: number;
    target: number;
    deadline: string;
    extraDiscount: number;
    color?: string | null;
  }>;
}

/**
 * Valori del campo `color` accettati dal backend MyF per le barre di avanzamento
 * (shippingBonus, bonusProgram). Usati direttamente come variante della
 * ProgressBar, senza mapping: `red` (lontano dall'obiettivo), `amber` (a meta'),
 * `green` (raggiunto). Stringa vuota, null o valore sconosciuto = colore
 * automatico in base alla percentuale.
 */
export const PROGRESS_COLOR_VALUES = ['red', 'amber', 'green'] as const;
export type ProgressColorValue = (typeof PROGRESS_COLOR_VALUES)[number];

export function parseProgressColor(raw: string | null | undefined): ProgressColorValue | null {
  const value = raw?.trim().toLowerCase();

  return (PROGRESS_COLOR_VALUES as readonly string[]).includes(value ?? '')
    ? (value as ProgressColorValue)
    : null;
}

export async function getCommercialConditions(
  company: string,
  customer: string,
): Promise<CommercialConditionsResponse> {
  // Endpoint live MyF "GET_CustomerTerms". Il proxy (Catalyst in prod, Vite in
  // dev) lo inoltra al backend Foscarini o al mock a seconda dell'ambiente.
  const res = await fetch(
    `${BASE}/Customers/${encodeURIComponent(company)}/${encodeURIComponent(customer)}/terms`,
    { headers: { 'Content-Type': 'application/json' } },
  );

  if (!res.ok) throw new Error(`MyF API ${res.status}: Customers terms`);

  return res.json() as Promise<CommercialConditionsResponse>;
}
